// An operator with the Pulse admin view, the support inbox and an employer page
// open in three windows heard the same approval three times: every window mounts
// its own <AgentRunNotificationListener />, opens its own Firestore listener,
// and rings on its own. Nothing coordinated them.
//
// So the windows race for a per-run claim and only the winner makes a sound. Web
// Locks are scoped per origin and shared across tabs, which is exactly the scope
// needed, and `ifAvailable` resolves the race in the browser rather than in a
// timing window we would have to guess at (the reason this is not a
// BroadcastChannel: message delivery is asynchronous, so two windows can both
// believe they were first).

// How long the winner keeps the claim. Every window gets the same snapshot
// within milliseconds and records the run in its own `seenRunIds`, so no window
// asks twice — the hold only has to outlast that fan-in, not the run.
const CLAIM_HOLD_MS = 10_000

export interface ClaimApprovalAlertOptions {
  holdMs?: number
  // Injectable for tests; jsdom has no LockManager.
  locks?: LockManager | null
}

function defaultLocks(): LockManager | null {
  if (typeof navigator === 'undefined' || !('locks' in navigator)) return null
  return navigator.locks
}

/**
 * Whether THIS window should play the alert for `runUid`.
 *
 * Resolves immediately — the winner does not wait out the hold before ringing.
 *
 * Fails open: with no LockManager, or if the request throws, this returns true.
 * An operator hearing a duplicate is a far cheaper failure than a blocked
 * payroll run nobody is told about, so the degraded path is today's behaviour
 * rather than silence.
 */
export function claimApprovalAlert(
  runUid: string,
  {
    holdMs = CLAIM_HOLD_MS,
    locks = defaultLocks(),
  }: ClaimApprovalAlertOptions = {}
): Promise<boolean> {
  if (!locks) return Promise.resolve(true)

  return new Promise<boolean>((resolve) => {
    let settled = false
    const settle = (won: boolean) => {
      if (settled) return
      settled = true
      resolve(won)
    }

    locks
      .request(
        `pulse-approval-alert-${runUid}`,
        { ifAvailable: true },
        (lock) => {
          if (!lock) {
            // Another window already claimed this run.
            settle(false)
            return
          }
          // Tell the caller it won now, then keep the lock so the windows still
          // working through the same snapshot see it as taken.
          settle(true)
          return new Promise<void>((release) => {
            setTimeout(release, holdMs)
          })
        }
      )
      .catch(() => settle(true))
  })
}
